import { inject, Injectable } from '@angular/core';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { LocalStorageService } from '@services/local-storage-service';
import { WindowRefService } from '@services/window-ref-service';
import { AppConfig } from '../../app/app.config';

/** Marks the location step as answered, so it is not asked again later in onboarding. */
const LOCATION_SEEN_STORAGE_KEY = 'Onboarding/locationPageSeen';

/**
 * Shared by the `/onboarding/location` page and the location bottomsheet. Grant and decline both
 * write `lastLocation` (decline writes `{}`) — that is what `Onboarding.hasLocationData()` checks.
 */
@Injectable({
  providedIn: 'root',
})
export class OnboardingLocationService {
  private readonly appWebBridge: AppWebBridgeService = inject(AppWebBridgeService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  private readonly storage: LocalStorageService = inject(LocalStorageService);
  private readonly windowRef: WindowRefService = inject(WindowRefService);
  private readonly appConfig: AppConfig = inject(AppConfig);

  /** Native bridge first, then the browser API. False means permission was refused. */
  async captureLocation(): Promise<boolean> {
    this.storage.setValue(LOCATION_SEEN_STORAGE_KEY, 'true');
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'onboardingLocationAccessGranted' }));

    if (this.appWebBridge.getLocationDetails()) return true;

    const geolocation = this.windowRef.nativeWindow?.navigator?.geolocation;
    if (!geolocation) return true;

    return new Promise<boolean>((resolve: (granted: boolean) => void): void => {
      geolocation.getCurrentPosition(
        async (location: GeolocationPosition): Promise<void> => {
          try {
            await this.conn.updateUserData({
              lastLocation: { latitude: location.coords.latitude, longitude: location.coords.longitude },
            });
            resolve(true);
          } catch (error) {
            this.broadcast.broadcast('NOTIFY', { message: String(error) });
            resolve(false);
          }
        },
        (): void => {
          this.broadcast.broadcast('NOTIFY', {
            message: 'Location permission denied. Kindly enable permission in your browser settings.',
          });
          resolve(false);
        },
      );
    });
  }

  /** Records that the user declined to share a location. */
  async skipLocation(): Promise<void> {
    this.storage.setValue(LOCATION_SEEN_STORAGE_KEY, 'true');
    await this.conn.updateUserData({ lastLocation: {} });
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'onboardingLocationAccessDenied' }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
  }
}
